import * as React from 'react';
import { compose, setDisplayName, lifecycle } from 'recompose';
import { connect } from 'react-redux';
import { Camera, CellIndex, Stream } from 'types';
import { State } from 'reducers';
import {
    isVideoConnecting, isVideoConnected,
    isPlaying, isLive, isSeeking,
    getStreamFromCell, getVideoError, getStillFrame, isMute,
} from 'selectors/monitoringSelectors';
import { isUsingWebsocket } from 'selectors/serverSelectors';
import { VideoView } from './VideoView';
import { VideoView as VideoViewWs } from './VideoViewWs';
import { PlaybackControls } from './controls/PlaybackControls';
import { PlaybackControls as PlaybackControlsWs } from './controls/PlaybackControlsWs';
import { ImageControls } from './controls/ImageControls';
import { Fade } from 'ui/transitions';
import { NoStream } from './NoStream';
import { Loading } from '../../../shared/Loading';
import { VideoFailure } from './VideoFailure';
import { StillFrameView } from './StillFrameView';
import { BannedVideo } from './BannedVideo';
import { getCamera } from 'selectors/deviceSelectors';
import { videoPlay } from 'actions/monitoringActions';


export const RawControllableVideoView = ({
    cellIndex,
    camera,
    stream,
    showVideo,
    connecting,
    connected,
    seeking,
    error,
    frame,
    canView,
    live,
    useWebSocket,
    audioEnabled,
}: OwnProps & StateProps) =>
    <div className="ctrl-video-view">
        <Fade visible={ canView && (!stream || (!connecting && !connected && !seeking && !frame && !error)) } duration={ 200 } unmountOnExit={ true }>
            <NoStream/>
        </Fade>
        <Fade visible={ !canView } duration={ 200 } unmountOnExit={ true }>
            <BannedVideo/>
        </Fade>

        <Fade visible={ showVideo && canView } duration={ 400 } unmountOnExit={ true }>
            { useWebSocket ?
                <VideoViewWs cellIndex={ cellIndex } stream={ stream! } visible={ showVideo }/> :
                <VideoView cellIndex={ cellIndex } stream={ stream! } visible={ showVideo }/>
            }
        </Fade>

        <Fade visible={ showVideo && connected && live && canView } duration={ 400 } unmountOnExit={ true }>
            <ImageControls cellIndex={ cellIndex } deviceId={ camera ? camera.deviceId : '' }/>
        </Fade>

        <Fade visible={ !!frame && canView } duration={ 400 } unmountOnExit={ true }>
            <StillFrameView cellIndex={ cellIndex } stream={ stream } frame={ frame }/>
        </Fade>

        <Fade visible={ !!stream && canView } duration={ 400 } unmountOnExit={ true }>
            { useWebSocket && audioEnabled ?
                <PlaybackControlsWs cellIndex={ cellIndex }/> :
                <PlaybackControls cellIndex={ cellIndex }/>
            }
        </Fade>

        <Fade visible={ seeking } duration={ 400 } unmountOnExit={ true } className="connecting">
            <Loading textKey="SeekingDotDotDot" size="small"/>
        </Fade>

        <Fade visible={ connecting } duration={ 400 } unmountOnExit={ true }>
            <Loading textKey="ConnectingDotDotDot" size="small"/>
        </Fade>

        <VideoFailure error={ error }/>
    </div>
;

export type OwnProps = {
    cellIndex: CellIndex;
    camera: Camera | null;
};
export type StateProps = {
    stream: Stream | null;
    showVideo: boolean;
    connected: boolean;
    connecting: boolean;
    seeking: boolean;
    live: boolean;
    error: Error | null;
    frame: string | null;
    canView: boolean;
    canViewLive: boolean;
    useWebSocket: boolean;
    audioEnabled: boolean;
    mute: boolean;
};
export type DispProps = {
    reconnect: () => void;
};

const mapStateToProps = (state: State, ownProps: OwnProps) => {
    const stream = getStreamFromCell(state, ownProps);
    const camera = stream && getCamera(state, { deviceId: stream.deviceId });
    const live = isLive(state, ownProps);
    const mute = isMute(state, ownProps);
    const canView =
        !camera ||
        (
            (live && camera.canViewLive) ||
            (!live && camera.canViewHistorical)
        );
    const useWebSocket = isUsingWebsocket(state);

    return {
        stream,
        showVideo: isPlaying(state, ownProps),
        connected: isVideoConnected(state, ownProps),
        connecting: isVideoConnecting(state, ownProps) && canView,
        seeking: isSeeking(state, ownProps) && canView,
        live,
        error: getVideoError(state, ownProps),
        frame: getStillFrame(state, ownProps),
        canView,
        canViewLive: !camera || camera.canViewLive,
        useWebSocket,
        audioEnabled: camera ? camera.canSupportAudioIn : false,
        mute,
    };
};

const mapDispatchToProps = (dispatch: Function, { cellIndex }: OwnProps) => ({
    reconnect: () => dispatch(videoPlay({ cellIndex, live: true })),
});

export const ControllableVideoView = compose<OwnProps & StateProps, OwnProps>(
    setDisplayName('ControllableVideoView'),
    connect<StateProps, DispProps, OwnProps>(mapStateToProps,  mapDispatchToProps),
    lifecycle<OwnProps & StateProps & DispProps, {}>({
        componentDidUpdate(oldProps) {
            // AIR-36774: Restore video after a camera becomes unbanned
            // (and the user had previously tried to open the video while still banned)
            const { canView, canViewLive, showVideo, reconnect } = this.props;
            if (!showVideo && canView && !oldProps.canView) {
                reconnect();
            } else if (!showVideo && !canView && canViewLive) {
                reconnect(); // AIR-37553: need to switch to live if not allowed to view historical
            }
        },
    }),
)(RawControllableVideoView);



// WEBPACK FOOTER //
// ./src/ui/pages/monitoring/mainPanel/ControllableVideoView.tsx